/**
 * Frontend Logger Service
 * Only logs in development mode, stays silent in production
 *
 * Development mode detection:
 * 1. Check if running on localhost/dev domains
 * 2. Check for debug URL parameters
 * 3. Check for development-specific meta tags
 * 4. Check window location hostname patterns
 */

export enum LogLevel {
	DEBUG = 0,
	INFO = 1,
	WARN = 2,
	ERROR = 3,
}

export interface LogEntry {
	timestamp: string;
	level: LogLevel;
	source: string;
	message: string;
	data?: any;
}

export class Logger {
	private static instance: Logger;
	private isDevMode: boolean;
	private minLogLevel: LogLevel = LogLevel.DEBUG;
	private logHistory: LogEntry[] = [];
	private maxHistorySize: number = 100;

	private constructor() {
		this.isDevMode = this.detectDevelopmentMode();

		if (this.isDevMode) {
			console.info("[Logger] Running in development mode - logging enabled");
			this.logEnvironmentInfo();
		}
	}

	public static getInstance(): Logger {
		if (!Logger.instance) {
			Logger.instance = new Logger();
		}
		return Logger.instance;
	}

	/**
	 * Detect if we're running in development mode
	 */
	private detectDevelopmentMode(): boolean {
		try {
			// 1. Check hostname patterns (localhost, dev domains, IP addresses)
			const hostname = window.location.hostname.toLowerCase();
			const devHostnames = [
				"localhost",
				"127.0.0.1",
				"0.0.0.0",
				"dev",
				"development",
				"staging",
			];

			if (
				devHostnames.some(
					(devHost) =>
						hostname.includes(devHost) ||
						hostname.endsWith(".dev") ||
						hostname.endsWith(".local") ||
						hostname.endsWith(".localhost")
				)
			) {
				return true;
			}

			// 2. Check for IP addresses (common in dev environments)
			if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
				return true;
			}

			// 3. Check URL parameters
			const urlParams = new URLSearchParams(window.location.search);
			if (
				urlParams.get("debug") === "true" ||
				urlParams.get("dev") === "true" ||
				urlParams.get("sap-ui-debug") === "true"
			) {
				return true;
			}

			// 4. Check meta tags
			const metaDebug = document
				.querySelector('meta[name="debug"]')
				?.getAttribute("content");
			if (metaDebug === "true") {
				return true;
			}

			// 5. Check for development build indicators
			const scripts = Array.from(document.querySelectorAll("script[src]"));
			const hasDevScripts = scripts.some((script) => {
				const src = (script as HTMLScriptElement).src;
				return src.includes("-dbg.") || src.includes("sap-ui-debug");
			});

			if (hasDevScripts) {
				return true;
			}

			// 6. Check port numbers (common dev ports)
			const port = window.location.port;
			const devPorts = ["3000", "8080", "4200", "5000", "8000", "9000"];
			if (devPorts.includes(port)) {
				return true;
			}

			// 7. Check for console availability and not minified code
			if (typeof console !== "undefined" && console.debug) {
				// Try to detect if we have source maps or unminified code
				const hasSourceMaps = performance
					.getEntriesByType("navigation")
					.some(
						(entry: any) =>
							entry.name?.includes("sourcemap") || entry.name?.includes(".map")
					);
				if (hasSourceMaps) {
					return true;
				}
			}

			return false;
		} catch (error) {
			// If detection fails, err on the side of caution (production)
			return false;
		}
	}

	/**
	 * Log environment information for debugging
	 */
	private logEnvironmentInfo(): void {
		if (!this.isDevMode) return;

		console.group("[Logger] Environment Information");
		console.log("Hostname:", window.location.hostname);
		console.log("Port:", window.location.port);
		console.log("Protocol:", window.location.protocol);
		console.log("User Agent:", navigator.userAgent);
		console.log("URL:", window.location.href);

		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.toString()) {
			console.log("URL Parameters:", Object.fromEntries(urlParams));
		}

		console.groupEnd();
	}

	/**
	 * Set minimum log level
	 */
	public setLogLevel(level: LogLevel): void {
		this.minLogLevel = level;
		this.debug("Logger", `Log level set to: ${LogLevel[level]}`);
	}

	/**
	 * Check if development mode is enabled
	 */
	public isDevelopmentMode(): boolean {
		return this.isDevMode;
	}

	/**
	 * Force enable/disable logging (useful for testing)
	 */
	public setDevelopmentMode(enabled: boolean): void {
		this.isDevMode = enabled;
		if (enabled) {
			console.info("[Logger] Development mode manually enabled");
		}
	}

	/**
	 * Add log entry to history
	 */
	private addToHistory(entry: LogEntry): void {
		if (!this.isDevMode) return;

		this.logHistory.push(entry);
		if (this.logHistory.length > this.maxHistorySize) {
			this.logHistory.shift();
		}
	}

	/**
	 * Create log entry
	 */
	private createLogEntry(
		level: LogLevel,
		source: string,
		message: string,
		data?: any
	): LogEntry {
		return {
			timestamp: new Date().toISOString(),
			level,
			source,
			message,
			data,
		};
	}

	/**
	 * Log with specified level
	 */
	private log(
		level: LogLevel,
		source: string,
		message: string,
		data?: any
	): void {
		if (!this.isDevMode || level < this.minLogLevel) {
			return;
		}

		const entry = this.createLogEntry(level, source, message, data);
		this.addToHistory(entry);

		const prefix = `[${entry.source}]`;
		const timestamp = new Date().toLocaleTimeString();

		switch (level) {
			case LogLevel.DEBUG:
				if (data !== undefined) {
					console.debug(`${timestamp} ${prefix} ${message}`, data);
				} else {
					console.debug(`${timestamp} ${prefix} ${message}`);
				}
				break;
			case LogLevel.INFO:
				if (data !== undefined) {
					console.info(`${timestamp} ${prefix} ${message}`, data);
				} else {
					console.info(`${timestamp} ${prefix} ${message}`);
				}
				break;
			case LogLevel.WARN:
				if (data !== undefined) {
					console.warn(`${timestamp} ${prefix} ${message}`, data);
				} else {
					console.warn(`${timestamp} ${prefix} ${message}`);
				}
				break;
			case LogLevel.ERROR:
				if (data !== undefined) {
					console.error(`${timestamp} ${prefix} ${message}`, data);
				} else {
					console.error(`${timestamp} ${prefix} ${message}`);
				}
				break;
		}
	}

	/**
	 * Debug level logging
	 */
	public debug(source: string, message: string, data?: any): void {
		this.log(LogLevel.DEBUG, source, message, data);
	}

	/**
	 * Info level logging
	 */
	public info(source: string, message: string, data?: any): void {
		this.log(LogLevel.INFO, source, message, data);
	}

	/**
	 * Warning level logging
	 */
	public warn(source: string, message: string, data?: any): void {
		this.log(LogLevel.WARN, source, message, data);
	}

	/**
	 * Error level logging
	 */
	public error(source: string, message: string, data?: any): void {
		this.log(LogLevel.ERROR, source, message, data);
	}

	/**
	 * Group logging (for organizing related logs)
	 */
	public group(source: string, title: string): void {
		if (!this.isDevMode) return;
		console.group(`[${source}] ${title}`);
	}

	/**
	 * End group logging
	 */
	public groupEnd(): void {
		if (!this.isDevMode) return;
		console.groupEnd();
	}

	/**
	 * Get log history
	 */
	public getHistory(): LogEntry[] {
		return [...this.logHistory];
	}

	/**
	 * Clear log history
	 */
	public clearHistory(): void {
		this.logHistory = [];
		this.debug("Logger", "Log history cleared");
	}

	/**
	 * Dump current logger state for debugging
	 */
	public dumpState(): void {
		if (!this.isDevMode) return;

		console.group("[Logger] Current State");
		console.log("Development Mode:", this.isDevMode);
		console.log("Min Log Level:", LogLevel[this.minLogLevel]);
		console.log("History Size:", this.logHistory.length);
		console.log("Max History Size:", this.maxHistorySize);
		console.groupEnd();
	}
}

/**
 * Convenience function to get logger instance
 */
export function getLogger(): Logger {
	return Logger.getInstance();
}
